import { Observable, OperatorFunction, race, throwError, zip } from 'rxjs';
import { filter, first, reduce, switchMap, take } from 'rxjs/operators';
import { AggregatableAction, FailActionForAggregation } from '@nx-ng/types';

export const aggregate = <
  T extends AggregatableAction,
  TAction1 extends AggregatableAction,
  TFailAction extends FailActionForAggregation
>(
  actions$: Observable<TAction1>,
  actionCount: number,
  failAction$: Observable<TFailAction>
): OperatorFunction<T, TAction1[]> => {
  const filterAction = (
    sourceAction: AggregatableAction,
    t: AggregatableAction
  ) =>
    t.correlationParams &&
    sourceAction.correlationParams &&
    t.correlationParams.correlationId ===
      sourceAction.correlationParams.correlationId &&
    t.correlationParams.parentActionType === sourceAction.type;

  const getAggregatedActions = (
    sourceAction: AggregatableAction
  ): Observable<TAction1[]> => {
    let a1$ = actions$.pipe(
      filter((a) => {
        return filterAction(sourceAction, a) || false;
      }),
      take(actionCount)
    );

    let f$ = failAction$.pipe(
      filter((a) => {
        return filterAction(sourceAction, a) || false;
      }),
      first(),
      switchMap((b) => {
        return throwError(b.error);
      })
    );

    const all = a1$.pipe(
      zip,
      reduce((agg: TAction1[], curr: TAction1[]) => {
        return [...agg, ...curr];
      }, [])
    );

    return race(all, f$);
  };

  return (source: Observable<AggregatableAction>) =>
    source.pipe(
      switchMap((sourceAction) => getAggregatedActions(sourceAction))
    );
};
